import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { compact, find, reject } from "lodash/fp";
import { DateTime } from "luxon";

import type {
  CustomVocabulary,
  PatchCustomVocabularyBody,
  PostCustomVocabularyBody,
} from "@app/analytics/types";
import { getUniqueKey } from "@app/analytics/utils";
import apiClient from "@app/common/api/apiClient";
import useAuth from "@app/common/auth/useAuth";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import { replaceWhere } from "@app/utils/arrays";

const EMPTY_VOCABULARY: CustomVocabulary[] = [];
const MAX_WORDS = 100;

const useCustomVocabulary = () => {
  const { user } = useAuth();
  const { workspaceId } = useWorkspace();
  const queryClient = useQueryClient();

  const listQueryKey = ["customVocabulary", user?.id, workspaceId];

  const listCustomVocabularyQuery = useQuery({
    queryKey: listQueryKey,
    queryFn: apiClient.customVocabulary.listCustomVocabulary,
    enabled: Boolean(user?.id),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  const createCustomVocabularyMutation = useMutation({
    mutationFn: async (vocab: PostCustomVocabularyBody[]) => {
      const successResponses = await Promise.all(
        vocab.map((item) => {
          try {
            return apiClient.customVocabulary.createCustomVocabulary(item);
          } catch (exception: any) {
            return Promise.resolve(null);
          }
        })
      );
      if (compact(successResponses).length !== vocab.length) {
        // throw error so that we can trigger the onError mutation handler and
        // expose the failure to the user
        throw Error("some words failed to create");
      }
    },
    onMutate: async (vocab: PostCustomVocabularyBody[]) => {
      if (!user) return;

      await queryClient.cancelQueries({ queryKey: listQueryKey });

      const newWords: CustomVocabulary[] = vocab.map((item) => ({
        id: getUniqueKey(), // random id, will get overriden when the query is invalidated
        ...item,
        addedByName: user.fullName,
        addedByUserId: user.id,
        createdOn: DateTime.now(),
      }));

      const data: CustomVocabulary[] =
        queryClient.getQueryData(listQueryKey) ?? [];
      queryClient.setQueryData(listQueryKey, [...data, ...newWords]);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: listQueryKey }),
  });

  const patchCustomVocabularyMutation = useMutation({
    mutationFn: (body: PatchCustomVocabularyBody) =>
      apiClient.customVocabulary.patchCustomVocabulary(body),
    onMutate: async (body: PatchCustomVocabularyBody) => {
      await queryClient.cancelQueries({ queryKey: listQueryKey });
      const data: CustomVocabulary[] =
        queryClient.getQueryData(listQueryKey) ?? [];
      const updatedWord = find({ id: body.id }, data);
      if (updatedWord) {
        const updatedData = replaceWhere(
          data,
          updatedWord,
          ({ id }) => id === body.id
        );
        queryClient.setQueryData(listQueryKey, updatedData);
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: listQueryKey }),
  });

  const deleteCustomVocabularyMutation = useMutation({
    mutationFn: apiClient.customVocabulary.deleteCustomVocabulary,
    onMutate: async (id: CustomVocabulary["id"]) => {
      await queryClient.cancelQueries({ queryKey: listQueryKey });
      const data: CustomVocabulary[] =
        queryClient.getQueryData(listQueryKey) ?? [];

      const filteredData = reject({ id }, data);
      queryClient.setQueryData(listQueryKey, filteredData);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: listQueryKey }),
  });

  const customVocabulary = listCustomVocabularyQuery.data ?? EMPTY_VOCABULARY;

  return {
    isLoading: listCustomVocabularyQuery.isInitialLoading,
    customVocabulary,
    canAdd:
      !listCustomVocabularyQuery.isInitialLoading &&
      customVocabulary.length < MAX_WORDS,
    maximumWords: MAX_WORDS,

    createCustomVocabulary: createCustomVocabularyMutation,
    updateCustomVocabulary: patchCustomVocabularyMutation,
    deleteCustomVocabulary: deleteCustomVocabularyMutation,
  };
};

export default useCustomVocabulary;
